import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, MoreHorizontalIcon, SettingsIcon, XIcon, PenIcon, PlusIcon, TrashIcon, ChevronDownIcon, CheckIcon, AlertTriangleIcon, ArchiveIcon, RefreshCcwIcon, CheckCircleIcon, XCircleIcon, UserPlusIcon, UploadIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../mocks/mockBills';
type TabType = 'due' | 'pending' | 'scheduled' | 'paid' | 'assigned';
interface Threshold {
  id: string;
  approver: string;
  amount: number;
  currency: string;
}
// Cycle day options
const CYCLE_DAY_OPTIONS = [{
  value: 1,
  label: '1st of the month'
}, {
  value: 5,
  label: '5th of the month'
}, {
  value: 10,
  label: '10th of the month'
}, {
  value: 15,
  label: '15th of the month'
}, {
  value: 20,
  label: '20th of the month'
}, {
  value: 25,
  label: '25th of the month'
}, {
  value: -1,
  label: 'Last day of the month'
}];
export function Bills() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('due');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(new Set());
  // Force re-read of mutable mockBills on every render
  const [refreshKey, setRefreshKey] = useState(0);
  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [paymentCycles, setPaymentCycles] = useState<number[]>(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) : [];
  });
  const [numberOfApprovals, setNumberOfApprovals] = useState(0);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  // Modal states
  const [showApprovalsModal, setShowApprovalsModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showCyclesModal, setShowCyclesModal] = useState(false);
  const [tempApprovals, setTempApprovals] = useState(0);
  const [tempThreshold, setTempThreshold] = useState({
    approver: 'Simon Verraest',
    amount: 0,
    currency: ''
  });
  const [tempCycles, setTempCycles] = useState<number[]>([]);
  // Action Modals State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectAction, setRejectAction] = useState<'archive' | 'due'>('archive');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAction, setCancelAction] = useState<'archive' | 'due'>('archive');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [actionTargetId, setActionTargetId] = useState<string | null>(null);
  // Current user for approval logic
  const currentUser = 'Simon Verraest';
  // Whether the current tab supports multi-select
  const showCheckboxes = activeTab !== 'paid';
  // Check if a bill is selectable (for pending tab, only bills the user can approve)
  const isBillSelectable = (bill: (typeof mockBills)[0]) => {
    if (activeTab === 'paid') return false;
    if (activeTab === 'pending') {
      // Only bills assigned to current user or unassigned (open-ended by rules)
      return !bill.approver || bill.approver === currentUser;
    }
    return true;
  };
  // Filter bills based on active tab and search
  const filteredBills = useMemo(() => {
    return mockBills.filter(bill => {
      const matchesSearch = searchQuery === '' || bill.billNumber.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierEmail.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      switch (activeTab) {
        case 'due':
          return bill.status === 'approved' || bill.status === 'overdue';
        case 'pending':
          return bill.status === 'pending_approval' && (!bill.approver || bill.approver === currentUser);
        case 'scheduled':
          return bill.status === 'approved' && bill.approvalStatus === 'approved';
        case 'paid':
          return bill.status === 'paid';
        case 'assigned':
          return bill.approver === 'Simon Verraest' && bill.status === 'pending_approval';
        default:
          return true;
      }
    });
  }, [activeTab, searchQuery, refreshKey]);
  // Refresh data when component mounts (e.g. after navigating back from approval wizard)
  useEffect(() => {
    setRefreshKey(k => k + 1);
  }, []);
  const groupedBills = useMemo(() => {
    const groups: Record<string, typeof filteredBills> = {
      'Due next month': [],
      'Due this month': [],
      Older: []
    };
    filteredBills.forEach(bill => {
      if (bill.dueDate.includes('2026-04')) {
        groups['Due next month'].push(bill);
      } else if (bill.dueDate.includes('2026-03')) {
        groups['Due this month'].push(bill);
      } else {
        groups['Older'].push(bill);
      }
    });
    return Object.entries(groups).filter(([_, bills]) => bills.length > 0);
  }, [filteredBills]);
  const handleSelectAllInGroup = (groupBills: typeof filteredBills) => {
    const selectableBills = groupBills.filter(isBillSelectable);
    const allSelected = selectableBills.every(b => selectedBillIds.has(b.id));
    const newSelected = new Set(selectedBillIds);
    if (allSelected) {
      selectableBills.forEach(b => newSelected.delete(b.id));
    } else {
      selectableBills.forEach(b => newSelected.add(b.id));
    }
    setSelectedBillIds(newSelected);
  };
  const toggleBillSelection = (id: string) => {
    const bill = mockBills.find(b => b.id === id);
    if (bill && !isBillSelectable(bill)) return;
    const newSelected = new Set(selectedBillIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedBillIds(newSelected);
  };
  const selectedBills = mockBills.filter(b => selectedBillIds.has(b.id));
  const totalSelectedAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  const handleSchedulePayments = () => {
    if (selectedBillIds.size === 0) return;
    const idsParam = Array.from(selectedBillIds).join(',');
    navigate(`/bill-approval/schedule?ids=${idsParam}`);
  };
  const handleApproveBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowApproveModal(true);
    setContextMenuOpenId(null);
  };
  const confirmApproveBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => approveBill(id, currentUser));
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowApproveModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleRejectBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowRejectModal(true);
    setContextMenuOpenId(null);
  };
  const confirmRejectBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (rejectAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due (approved status without schedule)
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowRejectModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleCancelScheduled = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowCancelModal(true);
    setContextMenuOpenId(null);
  };
  const confirmCancelScheduled = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (cancelAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowCancelModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleArchiveBill = (id: string) => {
    rejectBill(id, currentUser, 'Archived');
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  const handleReassignBill = (id: string) => {
    // Mock reassignment by clearing the approver so it goes back to general pending pool
    updateBill(id, {
      approver: undefined
    });
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  // Whether the current tab shows approve/reject actions
  const isApprovalTab = activeTab === 'assigned' || activeTab === 'pending';
  // Get summary label based on active tab
  const getSummaryLabel = () => {
    switch (activeTab) {
      case 'assigned':
      case 'pending':
        return 'Bills to review';
      case 'scheduled':
        return 'Scheduled payments';
      default:
        return 'Payments to schedule';
    }
  };
  const summaryLabel = getSummaryLabel();
  const handleSaveApprovals = () => {
    setNumberOfApprovals(tempApprovals);
    setShowApprovalsModal(false);
  };
  const handleSaveThreshold = () => {
    if (!tempThreshold.currency) return;
    setThresholds(prev => [...prev, {
      id: Date.now().toString(),
      approver: tempThreshold.approver,
      amount: tempThreshold.amount,
      currency: tempThreshold.currency
    }]);
    setTempThreshold({
      approver: 'Simon Verraest',
      amount: 0,
      currency: ''
    });
    setShowThresholdModal(false);
  };
  const removeThreshold = (id: string) => {
    setThresholds(prev => prev.filter(t => t.id !== id));
  };
  // Persist cycles to localStorage
  const handleSaveCycles = () => {
    localStorage.setItem('billPaymentCycles', JSON.stringify(tempCycles));
    setPaymentCycles(tempCycles);
    setShowCyclesModal(false);
  };
  const toggleCycleDay = (day: number) => {
    setTempCycles(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      if (prev.length >= 2) return prev; // max 2
      return [...prev, day].sort((a, b) => a - b);
    });
  };
  const formatCycleLabel = (cycles: number[]) => {
    if (cycles.length === 0) return 'No payment cycles';
    return cycles.map(d => {
      const opt = CYCLE_DAY_OPTIONS.find(o => o.value === d);
      return opt ? opt.label : '';
    }).join(' & ');
  };
  return <div className="max-w-[1400px] mx-auto" data-id="element-4026">
      {/* Header */}
      <div className="flex justify-between items-center mb-8" data-id="element-4027">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4028">
          {t('pages.Bills.bills')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-4029">
          <button onClick={() => setShowSettings(true)} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-4030">
            <SettingsIcon size={18} data-id="element-4031" />
          </button>
          <button onClick={() => navigate('/bill-bulk-upload')} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-4032">
            <UploadIcon size={16} data-id="element-4033" />
            {t('pages.Bills.upload')}
          </button>
          <button onClick={() => navigate('/bill-form')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4034">
            {t('pages.Bills.add_bill')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start" data-id="element-4035">
        {/* Main Content */}
        <div className="xl:col-span-3 space-y-6" data-id="element-4036">
          {/* Tabs + Controls */}
          <div className="flex items-end justify-between border-b border-gray-200" data-id="element-4037">
            <div className="flex space-x-6" role="tablist" aria-label="Bills" data-id="element-4038">
              {[{
              id: 'due',
              label: 'Due'
            }, {
              id: 'pending',
              label: 'Pending approval'
            }, {
              id: 'scheduled',
              label: 'Scheduled'
            }, {
              id: 'paid',
              label: 'Paid'
            }, {
              id: 'assigned',
              label: 'Assigned to me'
            }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`bills-panel-${tab.id}`} id={`bills-tab-${tab.id}`} onClick={() => {
              setActiveTab(tab.id as TabType);
              setSelectedBillIds(new Set());
            }} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A] text-[#C41B14]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4039">
                  {tab.label}
                </button>)}
            </div>
            <div className="flex items-center gap-4 pb-3" data-id="element-4040">
              <div className="relative" data-id="element-4041">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-4042" />

                <input type="search" aria-label={t('pages.Bills.attrs.search_bills')} placeholder={t('pages.Bills.attrs.search_bills')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4043" />
              </div>
              <label className="flex items-center cursor-pointer" data-id="element-4044">
                <div className="relative" data-id="element-4045">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-4046" />

                  <div className={`block w-9 h-5 rounded-full transition-colors ${showArchived ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-4047" />

                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-4048" />
                </div>
                <span className="text-xs text-gray-600 ml-2 font-medium" data-id="element-4049">
                  {t('pages.Bills.show_archived')}
                </span>
              </label>
            </div>
          </div>

          {/* Bills List */}
          <div className="space-y-8" data-id="element-4050">
            {groupedBills.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-4051">
                <p className="text-gray-500 text-sm" data-id="element-4052">
                  {t('pages.Bills.no_bills_found_matching_your_criteria')}
                </p>
              </div> : groupedBills.map(([groupName, groupBills]) => <div key={groupName} className="space-y-3" data-id="element-4053">
                  <div className="flex items-center gap-3" data-id="element-4054">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-4055">
                      {groupName}
                    </h3>
                    {showCheckboxes && <button onClick={() => handleSelectAllInGroup(groupBills)} className="text-xs font-medium text-[#C41B14] hover:text-[#9F140F]" data-id="element-4056">
                        {t('pages.Bills.select')}
                      </button>}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-4057">
                    <table className="w-full text-left border-collapse" data-id="element-4058">
                      <tbody className="divide-y divide-gray-100" data-id="element-4059">
                        {groupBills.map(bill => {
                    const selectable = isBillSelectable(bill);
                    return <tr key={bill.id} className={`hover:bg-gray-50 transition-colors ${selectedBillIds.has(bill.id) ? 'bg-yellow-50/50' : ''}`} data-id="element-4060">
                              {showCheckboxes && <td className="px-4 py-4 w-12" data-id="element-4061">
                                  {selectable ? <input type="checkbox" checked={selectedBillIds.has(bill.id)} onChange={() => toggleBillSelection(bill.id)} className="w-4 h-4 text-[#E2231A] border-gray-300 rounded focus:ring-[#E2231A] cursor-pointer" data-id="element-4062" /> : <input type="checkbox" disabled className="w-4 h-4 border-gray-200 rounded opacity-30 cursor-not-allowed" data-id="element-4063" />}
                                </td>}
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4064">
                                <div className="text-sm font-semibold text-gray-900" data-id="element-4065">
                                  {bill.supplierName}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5" data-id="element-4066">
                                  {bill.billNumber}
                                </div>
                              </td>
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4067">
                                <div className="text-sm font-medium text-gray-900" data-id="element-4068">
                                  {t('pages.BillApprovalWizard.usd')}{' '}
                                  {bill.amount.toLocaleString('en-US', {
                            minimumFractionDigits: 2
                          })}
                                </div>
                              </td>
                              {activeTab === 'paid' ? <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4069">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-4070">
                                      {t('pages.Bills.source')}
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-4071">
                                      {bill.paymentSource === 'balance' ? 'Balance' : bill.paymentSource === 'card' ? 'Card' : bill.paymentSource === 'bank_transfer' ? 'Bank Transfer' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4072">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-4073">
                                      {t('pages.Bills.payout')}
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-4074">
                                      {bill.payoutType === 'bank_account' ? 'Bank Account' : bill.payoutType === 'promptpay' ? 'PromptPay' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4075">
                                    <div className="text-sm text-gray-500" data-id="element-4076">
                                      {t('pages.BillDetail.paid_on')}{' '}
                                      {bill.paidDate ? new Date(bill.paidDate).toLocaleDateString('en-GB') : '—'}
                                    </div>
                                  </td>
                                </> : <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4077">
                                    <div className="flex flex-col items-start gap-1" data-id="element-4078">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4079">
                                        {bill.status === 'pending_approval' ? 'Pending' : bill.status === 'approved' ? 'Due' : bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                                      </span>
                                      {bill.status === 'pending_approval' && numberOfApprovals > 1 && <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100" data-id="element-4080">
                                            {t('pages.Bills.requires')}{' '}
                                            {numberOfApprovals}{' '}
                                            {t('pages.Bills.approvals')}
                                          </span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-4081">
                                    <div className="text-sm text-gray-500" data-id="element-4082">
                                      {t('pages.Bills.due_on')}{' '}
                                      {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                                    </div>
                                  </td>
                                </>}
                              <td className="px-4 py-4 text-right w-12 relative" data-id="element-4083">
                                {activeTab !== 'paid' && <>
                                    <button onClick={e => {
                            e.stopPropagation();
                            setContextMenuOpenId(contextMenuOpenId === bill.id ? null : bill.id);
                          }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" aria-label="Bill actions" data-id="element-4084">
                                      <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-4085" />
                                    </button>

                                    {contextMenuOpenId === bill.id && <>
                                        <div className="fixed inset-0 z-10" onClick={e => {
                              e.stopPropagation();
                              setContextMenuOpenId(null);
                            }} data-id="element-4086" />

                                        <div className="absolute right-8 top-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 animate-in fade-in zoom-in-95 duration-100" data-id="element-4087">
                                          {activeTab === 'due' && <button onClick={e => {
                                e.stopPropagation();
                                handleArchiveBill(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4088">
                                              <ArchiveIcon size={14} className="mr-2" data-id="element-4089" />

                                              {t('pages.Bills.archive_bill')}
                                            </button>}

                                          {activeTab === 'pending' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4090">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-4091" />

                                                {t('pages.Bills.approve')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4092">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-4093" />

                                                {t('pages.BillApprovalWizard.cancel')}
                                              </button>
                                            </>}

                                          {activeTab === 'scheduled' && <button onClick={e => {
                                e.stopPropagation();
                                handleCancelScheduled(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4094">
                                              <XCircleIcon size={14} className="mr-2" data-id="element-4095" />

                                              {t('pages.Bills.cancel_payment')}
                                            </button>}

                                          {activeTab === 'assigned' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4096">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-4097" />

                                                {t('pages.Bills.approve')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleReassignBill(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4098">
                                                <UserPlusIcon size={14} className="mr-2 text-blue-600" data-id="element-4099" />

                                                {t('pages.Bills.reassign')}
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-4100">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-4101" />

                                                {t('pages.BillApprovalWizard.cancel')}
                                              </button>
                                            </>}
                                        </div>
                                      </>}
                                  </>}
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>)}
          </div>
        </div>

        {/* Right Sidebar (Summary) */}
        <div className="xl:col-span-1" data-id="element-4102">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 sticky top-6" data-id="element-4103">
            <div className="flex justify-between items-start mb-6" data-id="element-4104">
              <div data-id="element-4105">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-4106">
                  {t('pages.BillApprovalWizard.summary')}
                </h2>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4107">
                  {t('pages.Bills.based_on_your_selected_bills')}
                </p>
              </div>
              {selectedBillIds.size > 0 && <button onClick={() => setSelectedBillIds(new Set())} className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-4108">
                  {t('pages.Bills.clear')}
                </button>}
            </div>
            <div className="space-y-4 mb-8" data-id="element-4109">
              <div className="flex justify-between items-center text-sm" data-id="element-4110">
                <span className="text-gray-600" data-id="element-4111">
                  {summaryLabel} - {selectedBillIds.size}
                </span>
                {selectedBillIds.size === 0 && <span className="text-gray-400 text-xs" data-id="element-4112">
                    {t('pages.Bills.no_selected_bills')}
                  </span>}
              </div>
              {selectedBillIds.size > 0 && <div className="flex justify-between items-center pt-4 border-t border-gray-200" data-id="element-4113">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4114">
                    {t('pages.BillDetail.total')}
                  </span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-4115">
                    {t('pages.BillApprovalWizard.usd')}{' '}
                    {totalSelectedAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>}
            </div>

            {isApprovalTab ? <div className="flex gap-2" data-id="element-4116">
                <button onClick={() => handleRejectBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-white border border-red-300 text-red-600 hover:bg-red-50 shadow-sm' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'}`} data-id="element-4117">
                  {t('pages.Bills.reject')}
                </button>
                <button onClick={() => handleApproveBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4118">
                  {t('pages.Bills.approve')}
                </button>
              </div> : activeTab === 'scheduled' ? <button onClick={() => handleCancelScheduled()} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4119">
                {t('pages.Bills.cancel_scheduled_payments')}
              </button> : <button onClick={handleSchedulePayments} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-4120">
                {t('pages.Bills.schedule_payments')}
              </button>}
          </div>
        </div>
      </div>

      {/* ========== ACTION MODALS ========== */}

      {/* Reject Modal */}
      {showRejectModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-4121" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4122">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4123">
              <div className="p-6" data-id="element-4124">
                <div className="flex items-center gap-3 mb-4" data-id="element-4125">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-4126">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-4127" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-4128">
                    {t('pages.Bills.reject')}{' '}
                    {actionTargetId ? 'bill' : `${selectedBillIds.size} bills`}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-4129">
                  {t('pages.Bills.what_would_you_like_to_do')}{' '}
                  {actionTargetId ? 'bill' : 'bills'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4130">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4131">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'archive'} onChange={() => setRejectAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-4132" />

                    <div className="ml-3" data-id="element-4133">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4134">
                        {t('pages.Bills.archive_bills')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4135">
                        {t('pages.Bills.remove_them_from_the_active_list')}
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'due' ? 'border-[#E2231A] bg-yellow-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4136">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'due'} onChange={() => setRejectAction('due')} className="mt-0.5 text-[#E2231A] focus:ring-[#E2231A]" data-id="element-4137" />

                    <div className="ml-3" data-id="element-4138">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4139">
                        {t('pages.Bills.move_back_to_due')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4140">
                        {t('pages.Bills.return_them_to_the_due_list')}
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-4141">
                  <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4142">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={confirmRejectBills} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4143">
                    {t('pages.Bills.confirm_rejection')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Approve Modal */}
      {showApproveModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApproveModal(false)} data-id="element-4144" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4145">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4146">
              <div className="p-6 text-center" data-id="element-4147">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-4148">
                  <CheckCircleIcon size={24} className="text-green-600" data-id="element-4149" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4150">
                  {t('pages.Bills.confirm_approval')}
                </h2>
                <p className="text-sm text-gray-600 mb-8" data-id="element-4151">
                  {t('pages.Bills.are_you_sure_you_want_to')}{' '}
                  {actionTargetId ? 'this bill' : `these ${selectedBillIds.size} bills`}
                  ?
                  {numberOfApprovals > 1 && ' This will count as 1 of the required approvals.'}
                </p>
                <div className="flex justify-center gap-3" data-id="element-4152">
                  <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4153">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={confirmApproveBills} className="px-4 py-2 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#EB4A42] transition-colors" data-id="element-4154">
                    {t('pages.Bills.confirm_approval')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Cancel Scheduled Modal */}
      {showCancelModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCancelModal(false)} data-id="element-4155" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4156">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-4157">
              <div className="p-6" data-id="element-4158">
                <div className="flex items-center gap-3 mb-4" data-id="element-4159">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-4160">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-4161" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-4162">
                    {t('pages.Bills.cancel_scheduled')}{' '}
                    {actionTargetId ? 'Payment' : 'Payments'}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-4163">
                  {t('pages.Bills.what_would_you_like_to_do_2')}{' '}
                  {actionTargetId ? 'payment' : 'payments'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-4164">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'due' ? 'border-[#E2231A] bg-yellow-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4165">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'due'} onChange={() => setCancelAction('due')} className="mt-0.5 text-[#E2231A] focus:ring-[#E2231A]" data-id="element-4166" />

                    <div className="ml-3" data-id="element-4167">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4168">
                        {t('pages.Bills.move_back_to_due')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4169">
                        {t('pages.Bills.return_them_to_the_due_list_2')}
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4170">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'archive'} onChange={() => setCancelAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-4171" />

                    <div className="ml-3" data-id="element-4172">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-4173">
                        {t('pages.Bills.archive_bills')}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-4174">
                        {t('pages.Bills.remove_them_from_the_active_list')}
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-4175">
                  <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4176">
                    {t('pages.Bills.keep_scheduled')}
                  </button>
                  <button onClick={confirmCancelScheduled} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4177">
                    {t('pages.Bills.confirm_cancellation')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== BILLS SETTINGS SLIDE-OUT PANEL ========== */}
      {showSettings && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowSettings(false)} data-id="element-4178" />

          {/* Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200" data-id="element-4179">
            <div className="p-8" data-id="element-4180">
              {/* Close button */}
              <div className="flex justify-end mb-6" data-id="element-4181">
                <button onClick={() => setShowSettings(false)} aria-label="Close settings" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4182">
                  <XIcon size={18} aria-hidden="true" data-id="element-4183" />
                </button>
              </div>

              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4184">
                {t('pages.Bills.bills_settings')}
              </h2>

              {/* Bill payments can be approved by */}
              <div className="mb-8" data-id="element-4185">
                <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-4186">
                  {t('pages.Bills.bill_payments_can_be_approved_by')}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed" data-id="element-4187">
                  {t('pages.Bills.users_in_these_user_groups_can')}
                </p>
              </div>

              {/* Billing Cycles */}
              <div className="mb-8" data-id="element-4188">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4189">
                  {t('pages.Bills.billing_cycles')}
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4190">
                  <span className="text-sm font-medium text-gray-900" data-id="element-4191">
                    {formatCycleLabel(paymentCycles)}
                  </span>
                  <button onClick={() => {
                setTempCycles([...paymentCycles]);
                setShowCyclesModal(true);
              }} aria-label={t('pages.Bills.edit_payment_cycles', {
                defaultValue: 'Edit payment cycles'
              })} className="text-[#C41B14] hover:text-[#9F140F] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-4192">
                    <PenIcon size={16} aria-hidden="true" data-id="element-4193" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-4194">
                  {t('pages.Bills.automate_your_payment_runs_by_setting')}
                </p>
              </div>

              {/* Number of Approvals */}
              <div className="mb-8" data-id="element-4195">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4196">
                  {t('pages.Bills.number_of_approvals')}
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4197">
                  <span className="text-sm font-medium text-gray-900" data-id="element-4198">
                    {numberOfApprovals}
                  </span>
                  <button onClick={() => {
                setTempApprovals(numberOfApprovals);
                setShowApprovalsModal(true);
              }} aria-label={t('pages.Bills.edit_number_of_approvals', {
                defaultValue: 'Edit number of approvals'
              })} className="text-[#C41B14] hover:text-[#9F140F] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-4199">
                    <PenIcon size={16} aria-hidden="true" data-id="element-4200" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-4201">
                  {t('pages.Bills.approving_any_payment_for_bills_will')}{' '}
                  {numberOfApprovals}{' '}
                  {t('pages.Bills.users_to_approve_except_when_threshold')}
                </p>
              </div>

              {/* Limits and Thresholds */}
              <div data-id="element-4202">
                <div className="flex items-center justify-between mb-2" data-id="element-4203">
                  <h3 className="text-xs font-medium text-gray-500" data-id="element-4204">
                    {t('pages.Bills.limits_and_thresholds')}
                  </h3>
                  <button onClick={() => {
                setTempThreshold({
                  approver: 'Simon Verraest',
                  amount: 0,
                  currency: ''
                });
                setShowThresholdModal(true);
              }} className="text-xs font-medium text-[#C41B14] hover:text-[#9F140F] transition-colors" data-id="element-4205">
                    {t('pages.Bills.add_threshold')}
                  </button>
                </div>

                {thresholds.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4206">
                    <span className="text-sm text-gray-500" data-id="element-4207">
                      {t('pages.Bills.no_specific_threshold_approvers_configured_yet')}
                    </span>
                  </div> : <div className="space-y-2" data-id="element-4208">
                    {thresholds.map(thr => <div key={thr.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-4209">
                        <div data-id="element-4210">
                          <div className="text-sm font-medium text-gray-900" data-id="element-4211">
                            {thr.approver}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-4212">
                            {t('pages.Bills.bills_above')} {thr.currency}{' '}
                            {thr.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2
                    })}
                          </div>
                        </div>
                        <button onClick={() => removeThreshold(thr.id)} className="text-[#C41B14] hover:text-[#9F140F] transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Remove threshold for ${thr.approver}`} data-id="element-4213">
                          <TrashIcon size={14} data-id="element-4214" />
                        </button>
                      </div>)}
                  </div>}

                <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-4215">
                  {t('pages.Bills.when_threshold_rules_are_configured_payments')}
                </p>
              </div>
            </div>
          </div>
        </>}

      {/* ========== NUMBER OF APPROVALS MODAL ========== */}
      {showApprovalsModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApprovalsModal(false)} data-id="element-4216" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4217">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4218">
              <div className="p-8" data-id="element-4219">
                <div className="flex justify-end mb-4" data-id="element-4220">
                  <button onClick={() => setShowApprovalsModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4221">
                    <XIcon size={18} aria-hidden="true" data-id="element-4222" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-4223">
                  {t('pages.Bills.number_of_approvals')}
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed" data-id="element-4224">
                  {t('pages.Bills.the_default_number_of_staff_members')}
                </p>

                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4225">
                  {t('pages.Bills.number_of_approvals')}
                </label>
                <input type="number" min="0" value={tempApprovals} onChange={e => setTempApprovals(parseInt(e.target.value) || 0)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4226" />

                <div className="flex justify-end gap-3 mt-8" data-id="element-4227">
                  <button onClick={() => setShowApprovalsModal(false)} className="px-6 py-2.5 text-sm font-medium text-[#C41B14] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4228">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveApprovals} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-400 rounded-lg hover:bg-gray-500 transition-colors" data-id="element-4229">
                    {t('pages.BillForm.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD THRESHOLD MODAL ========== */}
      {showThresholdModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowThresholdModal(false)} data-id="element-4230" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4231">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4232">
              <div className="p-8" data-id="element-4233">
                <div className="flex justify-end mb-4" data-id="element-4234">
                  <button onClick={() => setShowThresholdModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4235">
                    <XIcon size={18} aria-hidden="true" data-id="element-4236" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4237">
                  {t('pages.Bills.add_threshold')}
                </h2>

                <div className="space-y-5" data-id="element-4238">
                  {/* Approver */}
                  <div data-id="element-4239">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4240">
                      {t('pages.Bills.approver_name')}
                    </label>
                    <input type="text" value={tempThreshold.approver} onChange={e => setTempThreshold({
                  ...tempThreshold,
                  approver: e.target.value
                })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4241" />
                  </div>

                  {/* Amount */}
                  <div data-id="element-4242">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4243">
                      {t('pages.Bills.this_user_will_need_to_approve')}
                    </label>
                    <input type="number" min="0" value={tempThreshold.amount} onChange={e => setTempThreshold({
                  ...tempThreshold,
                  amount: parseFloat(e.target.value) || 0
                })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4244" />
                  </div>

                  {/* Currency */}
                  <div data-id="element-4245">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-4246">
                      {t('pages.Bills.select_a_currency_for_this_threshold')}
                    </label>
                    <div className="relative" data-id="element-4247">
                      <select value={tempThreshold.currency} onChange={e => setTempThreshold({
                    ...tempThreshold,
                    currency: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4248">
                        <option value="" disabled data-id="element-4249">
                          {t('pages.Bills.select_a_currency')}
                        </option>
                        <option value="MYR" data-id="element-4250">{t('pages.Bills.myr')}</option>
                        <option value="MYR" data-id="element-4251">MYR</option>
                        <option value="EUR" data-id="element-4252">{t('pages.Bills.eur')}</option>
                        <option value="GBP" data-id="element-4253">{t('pages.Bills.gbp')}</option>
                        <option value="SGD" data-id="element-4254">{t('pages.Bills.sgd')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4255">
                  <button onClick={() => setShowThresholdModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4256">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveThreshold} disabled={!tempThreshold.currency} className="px-6 py-2.5 text-sm font-medium text-[#C41B14] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4257">
                    {t('pages.Bills.add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD CYCLES MODAL ========== */}
      {showCyclesModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCyclesModal(false)} data-id="element-4258" />

          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-4259">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-4260">
              <div className="p-8" data-id="element-4261">
                <div className="flex justify-end mb-4" data-id="element-4262">
                  <button onClick={() => setShowCyclesModal(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-4263">
                    <XIcon size={18} aria-hidden="true" data-id="element-4264" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-4265">
                  {t('pages.Bills.payment_cycles')}
                </h2>

                <div className="space-y-4" data-id="element-4266">
                  {CYCLE_DAY_OPTIONS.map(opt => <label key={opt.value} className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-4267">
                      <input type="checkbox" checked={tempCycles.includes(opt.value)} onChange={() => toggleCycleDay(opt.value)} disabled={!tempCycles.includes(opt.value) && tempCycles.length >= 2} className="w-4 h-4 text-[#E2231A] border-gray-300 rounded focus:ring-[#E2231A] cursor-pointer" data-id="element-4268" />

                      <span className="ml-3 text-sm text-gray-700" data-id="element-4269">
                        {opt.label}
                      </span>
                    </label>)}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4270">
                  <button onClick={() => setShowCyclesModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4271">
                    {t('pages.BillApprovalWizard.cancel')}
                  </button>
                  <button onClick={handleSaveCycles} className="px-6 py-2.5 text-sm font-medium text-[#C41B14] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4272">
                    {t('pages.Bills.save_cycles')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}